import { useQuery } from "@tanstack/react-query";
import { CircleAlert, LoaderCircle } from "lucide-react";
import { useMemo, useState } from "react";

import { api, type TransactionList } from "@/client";
import { MetricStrip } from "@/components/MetricStrip";
import { TransactionFilters, type ClassificationFilter } from "@/components/TransactionFilters";
import { TransactionWorkspace } from "@/components/TransactionWorkspace";
import { Movements, type MovementDirection } from "@/domain/movements";
import { Receipts } from "@/domain/receipts";
import { FinancePresentation } from "@/presentation/finance";

const movementTotalLabels: Record<MovementDirection, string> = {
	DEBIT: "Saídas no mês",
	CREDIT: "Entradas no mês",
	ALL: "Volume movimentado",
};

export function MovementsView({ data }: { data: TransactionList }) {
	const [direction, setDirection] = useState<MovementDirection>("DEBIT");
	const [classification, setClassification] = useState<ClassificationFilter>("ALL");
	const [search, setSearch] = useState("");
	const receiptsQuery = useQuery({
		queryKey: ["receipts", "current-month"],
		queryFn: () => api.receipts.list(),
	});
	const receiptClassifications = useMemo(
		() =>
			new Map(
				Receipts.classifications(receiptsQuery.data?.groups ?? []).map((item) => [
					item.transactionId,
					item,
				]),
			),
		[receiptsQuery.data?.groups],
	);
	const filtered = useMemo(() => {
		const normalizedSearch = search.trim().toLocaleLowerCase("pt-BR");

		return data.transactions.filter((transaction) => {
			const receiptClassification = receiptClassifications.get(transaction.id);
			const isClassified =
				!!transaction.classification || !!transaction.payableObligation || !!receiptClassification;

			if (direction !== "ALL" && transaction.direction !== direction) {
				return false;
			}
			if (classification === "PENDING" && isClassified) {
				return false;
			}
			if (classification === "CLASSIFIED" && !isClassified) {
				return false;
			}
			if (!normalizedSearch) {
				return true;
			}

			return [
				transaction.description,
				transaction.descriptionRaw,
				transaction.receiverName,
				transaction.supplier?.name,
				transaction.supplier?.legalName,
				transaction.classification?.explanation,
				transaction.classification?.destination,
				transaction.payableObligation?.description,
				transaction.payableObligation?.destination,
				transaction.payableObligation?.creditorName,
				receiptClassification?.explanation,
				receiptClassification?.destination,
				transaction.treasuryTransfer?.fromAccount.name,
				transaction.treasuryTransfer?.toAccount.name,
			]
				.filter(Boolean)
				.join(" ")
				.toLocaleLowerCase("pt-BR")
				.includes(normalizedSearch);
		});
	}, [classification, data.transactions, direction, receiptClassifications, search]);
	const movementSummary = Movements.summarize({
		transactions: data.transactions,
		direction,
		inferredClassifications: receiptClassifications,
	});
	const receiptClassificationReady = direction === "DEBIT" || !!receiptsQuery.data;
	const receiptClassificationDetail = receiptsQuery.error
		? "Guardian indisponível para as entradas"
		: "consultando o A Receber";

	return (
		<>
			{receiptsQuery.isPending && (
				<div className="mb-4 flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-3 text-xs text-stone-500">
					<LoaderCircle className="size-4 animate-spin" />
					Atualizando a classificação das entradas com o contas a receber…
				</div>
			)}
			{receiptsQuery.error && (
				<div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-800">
					<CircleAlert className="size-4" />
					Os contadores de entradas não puderam consultar o Guardian: {receiptsQuery.error.message}
				</div>
			)}
			<div className="mb-4">
				<MetricStrip
					items={[
						{
							label: movementTotalLabels[direction],
							value: FinancePresentation.money(movementSummary.totalAmountCents),
							detail: `${movementSummary.totalCount} transações`,
						},
						{
							label: "Pendentes",
							value: receiptClassificationReady ? String(movementSummary.pendingCount) : "—",
							detail: receiptClassificationReady
								? `${FinancePresentation.money(movementSummary.pendingAmountCents)} aguardando`
								: receiptClassificationDetail,
							tone: "attention",
						},
						{
							label: "Classificadas",
							value: receiptClassificationReady ? String(movementSummary.classifiedCount) : "—",
							detail: receiptClassificationReady
								? `${FinancePresentation.money(movementSummary.classifiedAmountCents)} explicados`
								: receiptClassificationDetail,
							tone: "success",
						},
						direction === "ALL"
							? {
									label: "Movimento líquido",
									value: FinancePresentation.signedMoney(movementSummary.netAmountCents),
									detail: "entradas menos saídas",
									tone: movementSummary.netAmountCents >= 0 ? "success" : "attention",
								}
							: {
									label: "Cobertura bancária",
									value: data.coverage?.to
										? FinancePresentation.transactionDate(data.coverage.to)
										: "—",
									detail: data.coverage
										? `${data.coverage.transactionCount} sincronizados`
										: "sem sincronização",
								},
					]}
				/>
			</div>

			<TransactionWorkspace
				transactions={filtered}
				inferredClassifications={receiptClassifications}
				filters={
					<TransactionFilters
						view="MOVEMENTS"
						direction={direction}
						classification={classification}
						search={search}
						visibleCount={filtered.length}
						onDirection={setDirection}
						onClassification={setClassification}
						onSearch={setSearch}
					/>
				}
			/>
		</>
	);
}
